import { Container, Sprite, type DestroyOptions, type Texture } from "pixi.js";
import { applyPlacement } from "@slot/presentation";
import type { Log, PresentationTaskRunner, ViewportStore } from "@slot/presentation";
import { CAPTAIN_TUNING } from "../../tuning/characterTuning";
import { CAPTAIN_LAYOUT, CAPTAIN_RIVE } from "./captainArt";

/** What the captain asks of his art (`SpineArtboard`: the Rive file's conversion, drawn into a texture of its own). */
export interface CaptainArt {
  readonly texture: Texture;
  resize(scale: number): void;
  trigger(name: string): void;
  advance(deltaSeconds: number): boolean;
  render(deltaSeconds: number): void;
  dispose(): void;
}

/** The round's moments the captain reacts to (`CaptainPlugin`). */
export interface CaptainReactions {
  /** A winning spin settled without a big win: he cheers. */
  win(): void;
  /** The big-win panel opens: he dances, his hat flying. */
  bigWin(): void;
}

/**
 * The pirate captain (the designer's `pirate-captain.riv`, played as its Spine conversion) behind the grid: his idle
 * loops with a coin flip among them (the art's controls), a cheer on a win, a dance on a big win, back to his idle after
 * each. His soles stand on the shape's spot (`CAPTAIN_TUNING.placement`); nothing shows until his art is in. He is drawn
 * on the UI's runner, at the tuning's frame rate: the same speed in every speed mode, paused and frame-stepped with the
 * game, and never work a round waits for.
 */
export class CaptainView extends Container implements CaptainReactions {
  private readonly runner: PresentationTaskRunner;
  private readonly log: Log;
  private readonly unsubscribe: () => void;
  private art: CaptainArt | null = null;
  private wins = 0;
  private bigWins = 0;
  /** Device pixels per composition unit where he stands (his texture's size follows it). */
  private pixelsPerUnit = 1;

  public constructor(input: { viewport: ViewportStore; runner: PresentationTaskRunner; log: Log }) {
    super({ label: "CharacterOverlayLayer" });
    this.runner = input.runner;
    this.log = input.log;
    this.unsubscribe = input.viewport.subscribe((v) => {
      applyPlacement(this, CAPTAIN_TUNING.placement, v.shape);
      this.art?.resize(this.textureScale());
    });
  }

  /** His art, loaded: drawn as it is now, then on the UI's runner for as long as he stands. */
  public setArt(art: CaptainArt): void {
    if (this.destroyed || this.art) {
      art.dispose();
      return;
    }
    const { artboard, soles } = CAPTAIN_LAYOUT;
    art.resize(this.textureScale());
    const sprite = new Sprite({ texture: art.texture, anchor: { x: soles.x / artboard.width, y: soles.y / artboard.height }, label: "Captain" });
    sprite.scale.set(CAPTAIN_TUNING.scale);
    art.render(0);
    this.addChild(sprite);
    this.art = art;
    this.log("[CaptainView] the captain is in");
    let last = 0;
    void this.runner.tickInScope(
      "",
      (elapsed) => {
        if (this.art === art) art.advance(elapsed - last);
        last = elapsed;
      },
      () => this.destroyed || this.art !== art,
    );
  }

  /** Device pixels per composition unit where he stands: his texture is drawn at his size on the screen. */
  public setPixelsPerUnit(pixelsPerUnit: number): void {
    if (!(pixelsPerUnit > 0) || pixelsPerUnit === this.pixelsPerUnit) return;
    this.pixelsPerUnit = pixelsPerUnit;
    this.art?.resize(this.textureScale());
  }

  public win(): void {
    if (!this.art) return;
    this.wins += 1;
    this.art.trigger(CAPTAIN_RIVE.win);
  }

  public bigWin(): void {
    if (!this.art) return;
    this.bigWins += 1;
    this.art.trigger(CAPTAIN_RIVE.bigWin);
  }

  /** Whether his art is in, and how many wins and big wins he has reacted to (the dev hook's read). */
  public get state(): { loaded: boolean; wins: number; bigWins: number } {
    return { loaded: this.art !== null, wins: this.wins, bigWins: this.bigWins };
  }

  public override destroy(options?: DestroyOptions): void {
    this.unsubscribe();
    const art = this.art;
    this.art = null;
    art?.dispose();
    super.destroy(options);
  }

  /** Texture pixels per artboard pixel: his size on the screen. */
  private textureScale(): number {
    return CAPTAIN_TUNING.scale * this.scale.x * this.pixelsPerUnit;
  }
}
